import { Tooltip } from 'antd'
import { CalendarOutlined, FieldNumberOutlined } from '@ant-design/icons'
import { ETAT_DEMANDE_COLOR } from '../../constants'
import dayjs from 'dayjs'
import DemandeDescriptionCell from './DemandeDescriptionCell'
import DemandeEpingleCell from './DemandeEpingleCell'
import { D4_Fn_View_Demande } from '../../types'
import { uniqBy } from 'lodash'
import { Bat, DateEvt, Equipement, Etat, Numero } from './DemandeTableColumn'
import { ColumnsType } from 'antd/lib/table'

// const getColumnSearchProps = ({
//   handleSearch,
//   handleReset,
// }: {
//   handleSearch: React.Dispatch<React.SetStateAction<string>>
//   handleReset: () => void
// }): ColumnType<D4_Fn_View_Demande> => ({
//   filterIcon: <SearchOutlined />,
//   filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }: any) => (
//     <div style={{ padding: 8 }}>
//       <Input
//         placeholder="Recherche"
//         value={selectedKeys[0]}
//         onChange={(e) => {
//           handleSearch(e.target.value.toLowerCase().trim())
//         }}
//       />
//       <Button
//         type="text"
//         onClick={() => {
//           setSelectedKeys([''])
//           handleReset()
//         }}
//       >
//         Effacer
//       </Button>
//     </div>
//   ),
// })

/**
 * Parse des descriptions pour garder que le 3eme groupe
 * le batiment et l'equipement dans la description sont redondant
 */
export const descReg = new RegExp(/(.*?> )(.*?> )(.*)/)

/**
 * Mapping pour les valeurs associé aux libelles dans les demandes pour la generation des filtres
 */
const IDINDEX: PartialRecord<SortList, string> = {
  batimentlibelle: 'batimentlibelle',
  typeequipementlibelle: 'typeequipementid',
  kanbantitre: 'kanbanid',
  etatkanbanlibelle: 'etatkanbanid',
}

/**
 * Mapping pour les sorts et filters
 */
const DATAINDEX = {
  COULEURETATKANBAN: 'couleuretatkanban',
  EPINGLE: 'epingle',
  BATIMENTLIBELLE: 'batimentlibelle',
  TYPEEQUIPEMENTLIBELLE: 'typeequipementlibelle',
  DESCRIPTION: 'description',
  DATEEVENEMENT: 'dateevenement',
  KANBANTITRE: 'kanbantitre',
  ETATKANBANLIBELLE: 'etatkanbanlibelle',
  NUMERO: 'numero',
}

/**
 * Crée les filtres a partir des demandes grace a la list des options
 * @param demandes Demandes[]
 */
export const getFilters = (
  demandes: D4_Fn_View_Demande[]
): Record<string, Array<{ text: string; value: any; dataIndex: string }>> => {
  const keys = Object.entries(IDINDEX)
  //@ts-expect-error
  return keys.reduce(
    //@ts-expect-error
    (
      acc: Record<string, Array<{ text: string; value: any; dataIndex: string }>>,
      cur: Record<number, keyof Omit<D4_Fn_View_Demande, 'evenements'>>
    ) => {
      acc[cur[0]] = uniqBy(
        demandes.map((dem) => ({ text: dem[cur[0]], value: dem[cur[1]], dataIndex: cur[1] })),
        'value'
      )
      return acc
    },
    {}
  )
}

export const generateColumns = ({
  filtersOptions,
  smallScreen,
  sorters,
  filters,
}: {
  filtersOptions: Record<string, any[]>
  smallScreen: boolean | undefined
  sorters: Sorter
  filters: Record<string, string[] | number[]>
}): ColumnsType<D4_Fn_View_Demande> => [
  {
    dataIndex: DATAINDEX.COULEURETATKANBAN,
    key: DATAINDEX.COULEURETATKANBAN,
    width: 3,
    render: (value) => ({
      props: { style: { backgroundColor: ETAT_DEMANDE_COLOR[value] } },
    }),
  },
  {
    dataIndex: DATAINDEX.EPINGLE,
    key: DATAINDEX.EPINGLE,
    sortOrder: sorters[DATAINDEX.EPINGLE],
    width: 40,
    responsive: ['md'],
    showSorterTooltip: false,
    sorter: { compare: (a, b): any => Number(a.epingle) - Number(b.epingle), multiple: 1 },
    render: (value, record) => record && <DemandeEpingleCell item={record} />,
  },
  {
    title: 'Bat',
    dataIndex: DATAINDEX.BATIMENTLIBELLE,
    key: DATAINDEX.BATIMENTLIBELLE,
    width: smallScreen ? '20%' : 90,
    sortOrder: sorters[DATAINDEX.BATIMENTLIBELLE],
    filters: filtersOptions[DATAINDEX.BATIMENTLIBELLE].sort((a, b) => a?.text?.localeCompare(b?.text)),
    onFilter: (value, record) => record?.batimentlibelle === value,
    filteredValue: filters[DATAINDEX.BATIMENTLIBELLE],
    sorter: {
      compare: (a, b) => {
        return a.batimentid - b.batimentid
      },
      multiple: 2,
    },
    showSorterTooltip: false,
    ellipsis: true,
    render: (value) => <Bat value={value} />,
  },
  {
    title: 'Équipement',
    dataIndex: DATAINDEX.TYPEEQUIPEMENTLIBELLE,
    key: DATAINDEX.TYPEEQUIPEMENTLIBELLE,
    sortOrder: sorters[DATAINDEX.TYPEEQUIPEMENTLIBELLE],
    // responsive: ['sm'],
    width: smallScreen ? '30%' : 150,
    showSorterTooltip: false,
    ellipsis: true,
    filters: filtersOptions[DATAINDEX.TYPEEQUIPEMENTLIBELLE].sort((a, b) => a?.text?.localeCompare(b?.text)),
    filteredValue: filters[DATAINDEX.TYPEEQUIPEMENTLIBELLE],
    onFilter: (value, record) => record?.typeequipementid === value,
    sorter: {
      compare: (a, b) =>
        (a.typeequipementlibelle &&
          b.typeequipementlibelle &&
          a.typeequipementlibelle.localeCompare(b.typeequipementlibelle)) ||
        0,
      multiple: 1,
    },
    render: (value) => <Equipement value={value} />,
  },
  {
    title: 'Description',
    dataIndex: DATAINDEX.DESCRIPTION,
    key: DATAINDEX.DESCRIPTION,
    width: smallScreen ? '50%' : '40%',
    // sortDirections: ['descend', 'ascend'],
    render: (text, record) => <DemandeDescriptionCell text={text} item={record} />,
    // showSorterTooltip: false,
    ellipsis: true,
  },
  {
    title: () => (
      <Tooltip title="Date Evenement">
        <CalendarOutlined />
      </Tooltip>
    ),
    dataIndex: DATAINDEX.DATEEVENEMENT,
    key: DATAINDEX.DATEEVENEMENT,
    sortOrder: sorters[DATAINDEX.DATEEVENEMENT],
    width: 60,
    ellipsis: true,
    sorter: {
      compare: (a, b) => dayjs(a.dateevenement).unix() - dayjs(b.dateevenement).unix(),
      multiple: 1,
    },
    render: (value) => <DateEvt value={value} />,
    responsive: ['md'],
    showSorterTooltip: false,
  },
  {
    title: 'Kanban',
    dataIndex: DATAINDEX.KANBANTITRE,
    key: DATAINDEX.KANBANTITRE,
    sortOrder: sorters[DATAINDEX.KANBANTITRE],
    filters: filtersOptions[DATAINDEX.KANBANTITRE].sort((a, b) => a?.text?.localeCompare(b?.text)),
    onFilter: (value, record) => record?.kanbanid === value,
    sorter: {
      compare: (a, b) => (a?.couleuretatkanban ?? -1) - (b?.couleuretatkanban ?? -1),
      multiple: 4,
    },
    filteredValue: filters[DATAINDEX.KANBANTITRE],
    // responsive: ['sm'],
    showSorterTooltip: false,
    ellipsis: {
      showTitle: false,
    },
    render: (value, record) => <Etat value={value} item={record} />,
  },
  {
    title: 'État',
    dataIndex: DATAINDEX.ETATKANBANLIBELLE,
    key: DATAINDEX.ETATKANBANLIBELLE,
    sortOrder: sorters[DATAINDEX.ETATKANBANLIBELLE],
    filters: filtersOptions[DATAINDEX.ETATKANBANLIBELLE].sort((a, b) => a?.text?.localeCompare(b?.text)),
    filteredValue: filters[DATAINDEX.ETATKANBANLIBELLE],
    onFilter: (value, record) => record?.etatkanbanid === value,
    sorter: {
      compare: (a, b) => (a?.etatKanbanNumeroOrdre ?? -1) - (b?.etatKanbanNumeroOrdre ?? -1),
      // compare: (a, b) => (a?.couleuretatkanban ?? -1) - (b?.couleuretatkanban ?? -1),
      multiple: 3,
    },
    // responsive: ['sm'],
    showSorterTooltip: false,
    ellipsis: {
      showTitle: false,
    },
    render: (value, record) => <Etat value={value} item={record} />,
  },
  {
    title: () => <FieldNumberOutlined />,
    width: 50,
    sortOrder: sorters[DATAINDEX.NUMERO],
    sorter: {
      compare: (a, b) => Number(a?.numero) - Number(b?.numero),
      multiple: 1,
    },
    dataIndex: DATAINDEX.NUMERO,
    key: DATAINDEX.NUMERO,
    showSorterTooltip: false,
    render: (value: number) => <Numero value={value} />,
    responsive: ['md'],
  },
]

export const SELECT_OPTION: Array<{ value: SelectValueList; label: string }> = [
  { value: 'chrono', label: 'Chronologique' },
  { value: 'batiment-equipement', label: 'Batiments / Equipements' },
  { value: 'travaux', label: 'Travaux et entretien Général' },
  { value: 'litige-occupant', label: 'Litige occupant' },
  { value: 'litige-fournisseur', label: 'Litige fournisseur' },
  { value: 'commandes-privatives', label: 'Commandes privatives' },
  { value: 'debloquer', label: 'A Débloquer' },
  { value: 'vhicules-ventouse', label: 'Véhicules ventouse' },
  { value: 'plaques-boites-aux-lettres', label: 'Plaques boites aux lettres' },
  { value: 'travaux-ag-hors-budget', label: 'Travaux AG, hors budget' },
  { value: 'sinistres', label: 'Sinistres' },
  { value: 'procédures', label: 'Procédures' },
  { value: 'dernier-comm-pas-d4', label: 'Dernier comm pas D4' },
]

export const SORT_FILTER_OPTION: Record<SelectValueList, FilterSorterInput> = {
  'dernier-comm-pas-d4': {
    sorter: {},
    filter: {}, // TODO
  },
  procédures: {
    sorter: { etatkanbanlibelle: 'ascend', batimentlibelle: 'ascend', typeequipementlibelle: 'ascend' },
    filter: { kanbantitre: [6944593] },
  },
  sinistres: {
    sorter: { etatkanbanlibelle: 'ascend', batimentlibelle: 'ascend', typeequipementlibelle: 'ascend' },
    filter: { kanbantitre: [5276952] },
  },
  'travaux-ag-hors-budget': {
    sorter: { etatkanbanlibelle: 'ascend', batimentlibelle: 'ascend', typeequipementlibelle: 'ascend' },
    filter: { kanbantitre: [4775157] },
  },
  'batiment-equipement': {
    sorter: { batimentlibelle: 'ascend', typeequipementlibelle: 'ascend' },
    filter: {},
  },
  travaux: {
    sorter: { etatkanbanlibelle: 'ascend', batimentlibelle: 'ascend', typeequipementlibelle: 'ascend' },
    filter: { kanbantitre: [3768202] },
  },
  'litige-occupant': {
    sorter: { etatkanbanlibelle: 'ascend', batimentlibelle: 'ascend', typeequipementlibelle: 'ascend' },
    filter: { kanbantitre: [3768851] },
  },
  'litige-fournisseur': {
    sorter: { etatkanbanlibelle: 'ascend', batimentlibelle: 'ascend', typeequipementlibelle: 'ascend' },
    filter: { kanbantitre: [3768858] },
  },
  'commandes-privatives': {
    sorter: { etatkanbanlibelle: 'ascend', batimentlibelle: 'ascend', typeequipementlibelle: 'ascend' },
    filter: { kanbantitre: [3805812] },
  },
  debloquer: {
    sorter: { etatkanbanlibelle: 'ascend', batimentlibelle: 'ascend', typeequipementlibelle: 'ascend' },
    filter: { kanbantitre: [3829908] },
  },
  'vhicules-ventouse': {
    sorter: { etatkanbanlibelle: 'ascend', batimentlibelle: 'ascend', typeequipementlibelle: 'ascend' },
    filter: { kanbantitre: [4026229] },
  },
  'plaques-boites-aux-lettres': {
    sorter: { etatkanbanlibelle: 'ascend', batimentlibelle: 'ascend', typeequipementlibelle: 'ascend' },
    filter: { kanbantitre: [4272559] },
  },
  chrono: {
    sorter: { dateevenement: 'descend', epingle: 'descend' },
    filter: {},
  },
}
